import { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useDebounce } from 'use-debounce';
import { ItemCard } from './items/item-card';
import { EditItemDialog } from './items/edit-item-dialog';
import {
  useInfiniteDepartmentsQuery,
  useInfiniteTouchpointsQuery,
  useInfiniteSubTouchpointsQuery,
  useInfiniteTitlesQuery,
  useDeleteDepartmentMutation,
  useDeleteTouchpointMutation,
  useDeleteSubTouchpointMutation,
  useDeleteTitleMutation,
} from '@/hooks/queries/useTemplates';
import { useInfiniteScrollFetch } from '@/hooks/useInfiniteScrollFetch';
import type {
  ApiDepartment,
  ApiTouchpoint,
  ApiSubTouchpoint,
  ApiTitle,
} from '@/types/templates';

interface ItemsListProps {
  columnType: 'department' | 'touchpoint' | 'subtouchpoint' | 'title';
  propertyTypeId: string | number;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onRefresh?: () => void;
}

type ApiItem = ApiDepartment | ApiTouchpoint | ApiSubTouchpoint | ApiTitle;

export function ItemsList({
  columnType,
  propertyTypeId,
  searchQuery,
  onSearchChange,
  onRefresh,
}: ItemsListProps) {
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  // Debounce search query with 500ms delay
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);

  // Track if we're searching to reset page
  const [isSearching, setIsSearching] = useState(false);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | number | null>(
    null
  );

  useEffect(() => {
    if (searchQuery !== debouncedSearchQuery) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
    }
  }, [searchQuery, debouncedSearchQuery]);

  // Only create the query for the active column type - fetch all items
  const departmentsQuery = useInfiniteDepartmentsQuery(
    propertyTypeId,
    { search: debouncedSearchQuery, page_size: 1000 },
    columnType === 'department'
  );
  const touchpointsQuery = useInfiniteTouchpointsQuery(
    propertyTypeId,
    { search: debouncedSearchQuery, page_size: 1000 },
    columnType === 'touchpoint'
  );
  const subTouchpointsQuery = useInfiniteSubTouchpointsQuery(
    propertyTypeId,
    { search: debouncedSearchQuery, page_size: 1000 },
    columnType === 'subtouchpoint'
  );
  const titlesQuery = useInfiniteTitlesQuery(
    propertyTypeId,
    { search: debouncedSearchQuery, page_size: 1000 },
    columnType === 'title'
  );

  // Delete mutations with refetch callbacks
  const deleteDepartmentMutation = useDeleteDepartmentMutation({
    onSuccessCallback: () => {
      onRefresh?.();
    },
  });
  const deleteTouchpointMutation = useDeleteTouchpointMutation({
    onSuccessCallback: () => {
      onRefresh?.();
    },
  });
  const deleteSubTouchpointMutation = useDeleteSubTouchpointMutation({
    onSuccessCallback: () => {
      onRefresh?.();
    },
  });
  const deleteTitleMutation = useDeleteTitleMutation({
    onSuccessCallback: () => {
      onRefresh?.();
    },
  });

  // Select the active query based on column type
  const activeQuery = useMemo(() => {
    switch (columnType) {
      case 'department':
        return departmentsQuery;
      case 'touchpoint':
        return touchpointsQuery;
      case 'subtouchpoint':
        return subTouchpointsQuery;
      case 'title':
        return titlesQuery;
      default:
        return departmentsQuery;
    }
  }, [
    columnType,
    departmentsQuery,
    touchpointsQuery,
    subTouchpointsQuery,
    titlesQuery,
  ]);

  // Use infinite scroll hook
  const {
    items,
    totalCount,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    sentinelRef,
  } = useInfiniteScrollFetch<ApiItem>({
    infiniteQuery: activeQuery,
    rootMargin: '100px',
    threshold: 0.1,
  });

  const handleDelete = (itemId: string | number) => {
    switch (columnType) {
      case 'department':
        deleteDepartmentMutation.mutate(itemId);
        break;
      case 'touchpoint':
        deleteTouchpointMutation.mutate(itemId);
        break;
      case 'subtouchpoint':
        deleteSubTouchpointMutation.mutate(itemId);
        break;
      case 'title':
        deleteTitleMutation.mutate(itemId);
        break;
    }
    // Don't call onDelete - mutation already shows toast
  };

  const handleEdit = (itemId: string | number) => {
    setEditingItemId(itemId);
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditingItemId(null);
  };

  const handleEditSuccess = () => {
    onRefresh?.();
  };

  const getColumnTitle = () => {
    switch (columnType) {
      case 'department':
        return t('templates.admin.columnDialog.departments', 'Departments');
      case 'touchpoint':
        return t('templates.admin.columnDialog.touchpoints', 'Touchpoints');
      case 'subtouchpoint':
        return t(
          'templates.admin.columnDialog.subTouchpoints',
          'Sub-Touchpoints'
        );
      case 'title':
        return t('templates.admin.columnDialog.titles', 'Titles');
      default:
        return 'Items';
    }
  };

  if (isLoading || isSearching) {
    return (
      <div className="text-center py-8">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">
          {t('loading', 'Loading...')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Search Input */}
      <div className="relative">
        <Search
          className={cn(
            'absolute top-2.5 w-4 h-4 text-muted-foreground',
            isRTL ? 'right-3' : 'left-3'
          )}
        />
        <Input
          type="text"
          placeholder={t(
            'templates.admin.columnDialog.searchPlaceholder',
            `Search ${columnType}s...`
          )}
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
          className={cn(isRTL ? 'pr-10' : 'pl-10')}
        />
      </div>

      {/* Header */}
      <div
        className={cn(
          'flex items-center justify-between',
          isRTL && 'flex-row-reverse'
        )}
      >
        <h4 className="text-sm font-medium">
          {getColumnTitle()} ({totalCount})
        </h4>
      </div>

      {/* Items List with Infinite Scroll */}
      <div className="max-h-96 overflow-y-auto space-y-2 border rounded-md p-2">
        {items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            {searchQuery
              ? t('templates.admin.columnDialog.noResults', 'No results found')
              : t(
                  'templates.admin.columnDialog.noItems',
                  `No ${columnType}s found`
                )}
          </div>
        ) : (
          <>
            {items.map(item => (
              <ItemCard
                key={item.id}
                item={{
                  id: String(item.id),
                  name: item.name,
                }}
                columnType={columnType}
                onEdit={() => handleEdit(item.id)}
                onDelete={() => handleDelete(item.id)}
              />
            ))}

            {/* Sentinel for infinite scroll */}
            {hasNextPage && (
              <div ref={sentinelRef} className="py-4 text-center">
                {isFetchingNextPage && (
                  <Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" />
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Dialog */}
      {editingItemId && (
        <EditItemDialog
          isOpen={editDialogOpen}
          onClose={handleEditDialogClose}
          columnType={columnType}
          itemId={editingItemId}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
}
